import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import { links } from "./SuperAdminPages";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, Ban, Pause, Play, X, Clock, Sparkles, AlertTriangle, BarChart3 } from "lucide-react";

export function ClinicSubscriptionPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeForm, setChangeForm] = useState({ plan_id: "", reason: "", start_trial: false, notes: "" });
  const [statusForm, setStatusForm] = useState({ action: null, reason: "", notes: "" });
  const [trialForm, setTrialForm] = useState({ extra_days: 7, reason: "" });
  const [trialOpen, setTrialOpen] = useState(false);
  const [loadError, setLoadError] = useState("");

  const refresh = useCallback(async () => {
    setLoadError("");
    try {
      const [sub, ps, cs] = await Promise.all([
        api.get(`/subscriptions/clinic/${id}`), api.get("/plans"), api.get("/admin/clinics"),
      ]);
      setData(sub.data);
      setPlans(ps.data.filter((p) => p.status === "active"));
      setClinic((cs.data || []).find((c) => c.clinic_id === id));
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Failed to load subscription");
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitChange = async () => {
    if (!changeForm.plan_id || !changeForm.reason) { toast.error("Pick a plan and provide a reason"); return; }
    try {
      await api.post(`/subscriptions/clinic/${id}/change-plan`, changeForm);
      toast.success("Plan updated");
      setChangeOpen(false);
      setChangeForm({ plan_id: "", reason: "", start_trial: false, notes: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitStatus = async () => {
    if (!statusForm.action || !statusForm.reason) return;
    try {
      await api.post(`/subscriptions/clinic/${id}/${statusForm.action}`, statusForm);
      toast.success("Status updated");
      setStatusForm({ action: null, reason: "", notes: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitTrial = async () => {
    if (!trialForm.reason) { toast.error("Reason required"); return; }
    try {
      await api.post(`/subscriptions/clinic/${id}/extend-trial`, trialForm);
      toast.success(`Trial extended by ${trialForm.extra_days} days`);
      setTrialOpen(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loadError) {
    return <DashboardLayout links={links} title="Clinic subscription"><div className="mg-card text-center"><p className="text-red-600">{loadError}</p><button className="mg-btn-secondary mt-3" onClick={refresh}>Retry</button></div></DashboardLayout>;
  }
  if (!data) {
    return <DashboardLayout links={links} title="Clinic subscription"><div className="mg-card text-sm text-mg-muted">Loading…</div></DashboardLayout>;
  }

  const { subscription: sub, plan, effective_features: feats, plan_changes, trial_remaining_days } = data;
  const enabledFeats = Object.entries(feats || {}).filter(([, v]) => v.enabled);
  const status = sub?.status || "active";

  return (
    <DashboardLayout links={links} title={`Subscription — ${clinic?.name || "Clinic"}`}>
      <Link to="/super-admin/clinics" className="text-xs text-mg-muted hover:text-mg-medical flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Back to clinics</Link>

      {/* Header card */}
      <div className="mg-card mb-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Current plan</div>
            <h2 className="text-2xl font-extrabold text-mg-navy">{plan?.name || "No plan"}</h2>
            <p className="text-xs text-slate-500 mt-1">{plan?.short_description}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className={`mg-status mg-status-${status === "active" ? "active" : status === "trial" ? "checked-in" : status === "suspended" || status === "disabled" ? "cancelled" : "confirmed"}`} data-testid="sub-status">
                {status.toUpperCase()}
              </span>
              {sub?.is_trial && (
                <span className="bg-green-50 text-mg-health text-xs font-bold rounded-full px-2.5 py-1 flex items-center gap-1" data-testid="trial-badge">
                  <Clock className="w-3 h-3" /> Trial · {trial_remaining_days ?? "?"} day(s) left
                </span>
              )}
              {plan?.monthly_price ? (
                <span className="text-xs text-slate-500">₹{Number(plan.monthly_price).toLocaleString()}/mo</span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <button onClick={() => setChangeOpen(true)} className="mg-btn-primary text-sm" data-testid="change-plan-btn">
              <Sparkles className="w-4 h-4" /> Change plan
            </button>
            {sub?.is_trial && (
              <button onClick={() => setTrialOpen(true)} className="text-xs text-mg-medical font-semibold hover:underline" data-testid="extend-trial-btn">
                <Clock className="w-3.5 h-3.5 inline" /> Extend trial
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <ActionTile icon={<Pause className="w-4 h-4" />} label="Suspend"
          color="amber" disabled={status === "suspended" || status === "disabled"}
          onClick={() => setStatusForm({ action: "suspend", reason: "", notes: "" })}
          testid="suspend-btn" />
        <ActionTile icon={<Ban className="w-4 h-4" />} label="Disable"
          color="red" disabled={status === "disabled"}
          onClick={() => setStatusForm({ action: "disable", reason: "", notes: "" })}
          testid="disable-btn" />
        <ActionTile icon={<Play className="w-4 h-4" />} label="Reactivate"
          color="green" disabled={status === "active"}
          onClick={() => setStatusForm({ action: "reactivate", reason: "", notes: "" })}
          testid="reactivate-btn" />
        <ActionTile icon={<X className="w-4 h-4" />} label="Cancel"
          color="slate" disabled={status === "cancelled"}
          onClick={() => setStatusForm({ action: "cancel", reason: "", notes: "" })}
          testid="cancel-btn" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Effective features */}
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy text-sm mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-mg-medical" /> Effective features ({enabledFeats.length})</h3>
          <div className="grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto text-xs">
            {enabledFeats.map(([k, v]) => (
              <div key={k} className="px-2 py-1 bg-slate-50 rounded text-mg-navy" data-testid={`feat-${k}`}>
                {k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                {v.limit ? <span className="text-mg-muted ml-1">({v.limit})</span> : null}
              </div>
            ))}
            {enabledFeats.length === 0 && <div className="col-span-2 text-mg-muted italic">No features enabled — clinic effectively disabled.</div>}
          </div>
        </div>

        {/* Plan change history */}
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy text-sm mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-mg-medical" /> Subscription history</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(plan_changes || []).map((c) => (
              <div key={c.change_id} className="text-xs border-l-2 border-mg-medical pl-2" data-testid={`history-${c.change_id}`}>
                <div className="font-semibold text-mg-navy">{c.change_type?.replace(/_/g, " ")}</div>
                <div className="text-mg-muted">{c.reason}</div>
                <div className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleString()}</div>
              </div>
            ))}
            {(plan_changes || []).length === 0 && <div className="text-mg-muted italic text-sm">No plan changes yet.</div>}
          </div>
        </div>
      </div>

      {/* Change plan modal */}
      {changeOpen && (
        <Modal title="Change plan" onClose={() => setChangeOpen(false)}>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">New plan</span>
              <select value={changeForm.plan_id} onChange={(e) => setChangeForm({ ...changeForm, plan_id: e.target.value })} className="mg-input mt-1" data-testid="change-plan-select">
                <option value="">— pick a plan —</option>
                {plans.map((p) => {
                  const label = `${p.name} · ₹${Number(p.monthly_price).toLocaleString()}/mo${p.visibility === "private" ? " (private)" : ""}`;
                  return <option key={p.plan_id} value={p.plan_id}>{label}</option>;
                })}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-mg-navy">
              <input type="checkbox" checked={changeForm.start_trial} onChange={(e) => setChangeForm({ ...changeForm, start_trial: e.target.checked })} data-testid="start-trial-check" /> Start free trial (if plan allows)
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Reason</span>
              <input value={changeForm.reason} onChange={(e) => setChangeForm({ ...changeForm, reason: e.target.value })} className="mg-input mt-1" placeholder="e.g. Upgrade to Pro for advanced analytics" data-testid="change-reason-input" />
            </label>
            {(() => {
              const cur = plan;
              const next = plans.find((p) => p.plan_id === changeForm.plan_id);
              if (!cur || !next) return null;
              const lost = Object.entries(cur.features || {}).filter(([k, v]) => v.enabled && !next.features?.[k]?.enabled);
              if (lost.length === 0) return null;
              return (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <div>
                    <div className="font-bold text-amber-800">Downgrade warning — these features will be disabled:</div>
                    <div className="mt-1 text-amber-700">{lost.map(([k]) => k.replace(/_/g, " ")).join(", ")}</div>
                  </div>
                </div>
              );
            })()}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setChangeOpen(false)} className="mg-btn-secondary text-sm">Cancel</button>
              <button onClick={submitChange} className="mg-btn-primary text-sm" data-testid="confirm-change-plan">Apply</button>
            </div>
          </div>
        </Modal>
      )}

      {statusForm.action && (
        <Modal title={`${statusForm.action.charAt(0).toUpperCase() + statusForm.action.slice(1)} clinic`} onClose={() => setStatusForm({ action: null, reason: "", notes: "" })}>
          <div className="space-y-3 text-sm">
            <p className="text-mg-secondary">This will <strong>{statusForm.action}</strong> the clinic immediately. Action is audit-logged.</p>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Reason</span>
              <input value={statusForm.reason} onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })} className="mg-input mt-1" placeholder="Why?" data-testid="status-reason-input" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Internal notes (optional)</span>
              <textarea value={statusForm.notes} onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })} className="mg-input mt-1" rows={2} />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setStatusForm({ action: null, reason: "", notes: "" })} className="mg-btn-secondary text-sm">Cancel</button>
              <button onClick={submitStatus} disabled={!statusForm.reason} className="mg-btn-primary text-sm disabled:opacity-50" data-testid={`confirm-status-${statusForm.action}`}>Confirm</button>
            </div>
          </div>
        </Modal>
      )}

      {trialOpen && (
        <Modal title="Extend trial" onClose={() => setTrialOpen(false)}>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Extra days</span>
              <input type="number" value={trialForm.extra_days} onChange={(e) => setTrialForm({ ...trialForm, extra_days: Number(e.target.value) })} className="mg-input mt-1" data-testid="trial-extra-days" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">Reason</span>
              <input value={trialForm.reason} onChange={(e) => setTrialForm({ ...trialForm, reason: e.target.value })} className="mg-input mt-1" placeholder="e.g. Customer requested more evaluation time" data-testid="trial-reason-input" />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setTrialOpen(false)} className="mg-btn-secondary text-sm">Cancel</button>
              <button onClick={submitTrial} className="mg-btn-primary text-sm" data-testid="confirm-extend-trial">Extend</button>
            </div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function ActionTile({ icon, label, color, disabled, onClick, testid }) {
  const colorMap = {
    amber: "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-50 hover:bg-red-100 text-red-700 border-red-200",
    green: "bg-green-50 hover:bg-green-100 text-mg-health border-green-200",
    slate: "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border font-semibold text-sm transition ${colorMap[color]} disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testid}
    >
      {icon} {label}
    </button>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-mg-navy">{title}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-mg-muted" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
